import type { AgentOptions } from '@newrelic/browser-agent/loaders/agent';
import type {
    NewRelicDistributedTracingConfig,
    NewRelicOriginsClientConfig,
    RequiredNewRelicBrowserMonitoringConfig,
} from '../../common/config/configTypes';

// Defaults applied before the caller's own distributed-tracing overrides.
const defaultDistributedTracingOptions: NewRelicDistributedTracingConfig = {
    enabled: true,
    cors_use_newrelic_header: true,
    cors_use_tracecontext_headers: true,
    allowed_origins: [],
};

/** Reduce a full URL down to its origin, for comparing/deduplicating against other origins. */
const getUrlOrigin = (url: string): string => new URL(url).origin;

/** Resolve the Milanote origins that Browser distributed tracing would target when enabled. */
const getNewRelicBrowserAllowedOrigins = ({ urlBase, collaboration, mediaServer }: NewRelicOriginsClientConfig) => {
    const origins = [getUrlOrigin(urlBase), getUrlOrigin(collaboration.url), getUrlOrigin(mediaServer.apiUrl)];

    return Array.from(new Set(origins));
};

/** Options accepted by `BrowserAgent`/`Agent`; `info`/`init` come from the package's own types. */
export type NewRelicBrowserAgentOptions = {
    init: NonNullable<AgentOptions['init']>;
    info: AgentOptions['info'];
    loader_config: {
        accountID: string;
        trustKey: string;
        agentID: string;
        licenseKey: string;
        applicationID: string;
    };
};

/** Build the typed options Milanote passes to the Browser agent. */
export const buildNewRelicBrowserAgentOptions = (
    browserMonitoring: RequiredNewRelicBrowserMonitoringConfig,
    origins: NewRelicOriginsClientConfig,
    distributedTracing: Omit<NewRelicDistributedTracingConfig, 'allowed_origins'>,
): NewRelicBrowserAgentOptions => {
    const { accountId, applicationId, licenseKey, performance, capturePayloads } = browserMonitoring;

    return {
        init: {
            distributed_tracing: {
                ...defaultDistributedTracingOptions,
                ...distributedTracing,
                allowed_origins: getNewRelicBrowserAllowedOrigins(origins),
            },
            // TODO Implement browser consent mode since being released in v1.303.0 of the New Relic Browser agent
            browser_consent_mode: { enabled: false },
            privacy: { cookies_enabled: true },
            ajax: { deny_list: ['bam.nr-data.net'], capture_payloads: capturePayloads },
            performance: { capture_measures: performance.captureMeasures },
            session_replay: { enabled: false }, // screen recording off; session_trace stays on (default)
        },
        info: {
            beacon: 'bam.nr-data.net',
            errorBeacon: 'bam.nr-data.net',
            licenseKey,
            applicationID: applicationId,
            sa: 1,
        },
        loader_config: {
            accountID: accountId,
            trustKey: accountId,
            agentID: applicationId,
            licenseKey,
            applicationID: applicationId,
        },
    };
};
